/**
 * Android update route — Play vs sideload.
 *
 * The Android shell asks the OS who installed it (InstallSourcePlugin.java).
 * 'play'      -> installed by Google Play: send users to the Play listing
 * 'sideload'  -> any other / no installer (adb, browser, file manager): APK
 * 'unknown'   -> the query failed: treated like 'play' (never push an APK at
 *                someone who may have come from the store)
 * Only call from the Android native shell; web/iOS must not call it.
 */

import { Browser } from '@capacitor/browser';
import InstallSource from '../plugins/installSource';

export const PLAY_PACKAGE = 'com.android.vending';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.iopho.reedle';
// reedle-site home page (reedle.app): hosts the platform badges. Not the APK.
export const SITE_DOWNLOAD_URL = 'https://reedle.app/';

/** Map an installer package name to a source. */
export function classifyInstaller(installer) {
  return installer === PLAY_PACKAGE ? 'play' : 'sideload';
}

/** @returns {Promise<'play'|'sideload'|'unknown'>} */
export async function getAndroidInstallSource() {
  try {
    const res = await InstallSource.getInstallSource();
    if (!res || typeof res.installer !== 'string') return 'unknown';
    return classifyInstaller(res.installer.trim());
  } catch {
    return 'unknown';
  }
}

/** UI plan for a source: which action the main button takes, and whether the small fallback link shows. */
export function getUpdateRoute(source) {
  if (source === 'sideload') return { primary: 'apk', showSiteLink: false };
  return { primary: 'play', showSiteLink: true }; // 'play' and 'unknown'
}

/**
 * Open an external URL from the native shell. NOTE: @capacitor/app's openUrl()
 * is not implemented on Android ("App.openUrl() is not implemented on android",
 * seen on device 2026-11-02), so use the Browser plugin (Custom Tab) and fall
 * back to window.open.
 */
export async function openExternal(url) {
  try {
    await Browser.open({ url });
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Open the Play listing (https; Play app / Custom Tab handles it). */
export function openPlayListing() {
  return openExternal(PLAY_STORE_URL);
}
